// Trust Scope Management Component
import AjaxUtils from "../../util/ajax_utils";
import { delay } from "../_ui/_utils/useUiUtils";
import { debounce } from 'lodash';
import { ApckTrustScopeEmpty } from "./trust_scope_empty";

export const ApckTrustScopeManagement = {
    components: {
        'apck-trust-scope-empty': ApckTrustScopeEmpty
    },
    data: function() {
        return {
            loading: false,
            submitting: false,
            
            // OSINT Scanning toggle
            osintScanningEnabled: false,
            osintScanningLoading: false,
            
            // Scope data
            scopeItems: [],
            totalCount: 0,
            currentPage: 1,
            totalPages: 0,
            
            // Licence info
            licence: {
                max_trust_scope: 0,
                used_trust_scope: 0,
                unlimited: true
            },
            
            // Add modal state
            addModal: {
                render: false,
                open: false,
                step: 'suggest', // 'suggest', 'manual', or 'confirm'
                // Suggest step
                loadingSuggestions: false,
                suggestions: [],
                selectedSuggestions: [],
                suggestionsSearch: '',
                // Manual step
                domains: '',
                validating: false,
                validationResults: [],
                validationComplete: false
            },
            
            // Table columns
            cols: [
                {
                    data: 'apex_domain',
                    heading: 'Apex Domain',
                    canSearch: true,
                    width: '30%'
                },
                {
                    data: 'added_by',
                    heading: 'Added By',
                    width: '20%'
                },
                {
                    data: 'added_at',
                    heading: 'Added',
                    ctype: 'datecolumn',
                    width: '20%'
                },
                {
                    data: 'enabled',
                    heading: 'Enabled',
                    ctype: 'boolcolumn',
                    width: '15%',
                    center: true
                }
            ],
            
            // Table actions
            actions: [
                {
                    title: 'Toggle Enabled',
                    icon: 'fa-toggle-on',
                    class: 'btn-secondary',
                    handler: this.toggleEnabled,
                    useOwnCol: true,
                    hideLabel: true
                },
                {
                    title: 'Remove',
                    icon: 'fa-trash',
                    class: 'btn-danger',
                    handler: this.removeScope,
                    useOwnCol: true,
                    hideLabel: true
                }
            ]
        };
    },
    computed: {
        remaining() {
            if (this.licence.unlimited) return Infinity;
            return Math.max(0, this.licence.max_trust_scope - this.licence.used_trust_scope);
        },
        validDomainsCount() {
            return this.addModal.validationResults.filter(r => r.valid).length;
        },
        invalidDomainsCount() {
            return this.addModal.validationResults.filter(r => !r.valid).length;
        },
        validatedManualDomains() {
            return this.addModal.validationResults.filter(r => r.valid);
        },
        allDomainsToAdd() {
            // Combine selected suggestions with validated manual domains
            const domains = [];
            
            // Add selected suggestions
            this.addModal.selectedSuggestions.forEach(s => {
                domains.push({
                    apex_domain: s.apex_domain,
                    source: 'Suggested',
                    sources: s.sources
                });
            });
            
            // Add validated manual domains (avoiding duplicates)
            this.validatedManualDomains.forEach(r => {
                const domain = r.apex_domain || r.original;
                if (!domains.some(d => d.apex_domain === domain)) {
                    domains.push({
                        apex_domain: domain,
                        source: 'Manual',
                        sources: []
                    });
                }
            });
            
            return domains;
        },
        canProceedToConfirm() {
            return true;
        },
        canSubmit() {
            if (this.allDomainsToAdd.length === 0) return false;
            if (!this.licence.unlimited && this.allDomainsToAdd.length > this.remaining) return false;
            return true;
        }
    },
    methods: {
        async loadScope() {
            const cmp = this;
            cmp.loading = true;
            
            await AjaxUtils.ajax_get({
                url: `/trust/scope/list?page=${cmp.currentPage}&limit=50`,
                success: function(data) {
                    if (data.success) {
                        cmp.scopeItems = data.items || [];
                        cmp.totalCount = data.results || 0;
                        cmp.totalPages = data.pages || 0;
                        if (data.licence) {
                            cmp.licence = data.licence;
                        }
                    } else {
                        cmp.$flash({ message: data.message, type: 'error' });
                    }
                },
                fail: function() {
                    cmp.$flash({ message: 'Failed to load trust scope', type: 'error' });
                },
                opts: {
                    dataType: 'json',
                    flash_form_errors: false,
                    flash_404_errors: false,
                    show_success_flash: false
                }
            });
            
            await delay(100);
            cmp.loading = false;
        },
        
        openAddModal() {
            this.addModal.step = 'suggest';
            this.addModal.domains = '';
            this.addModal.validationResults = [];
            this.addModal.validationComplete = false;
            this.addModal.suggestions = [];
            this.addModal.selectedSuggestions = [];
            this.addModal.suggestionsSearch = '';
            this.addModal.loadingSuggestions = false;
            this.addModal.render = true;
            this.$nextTick(() => {
                this.addModal.open = true;
                this.loadSuggestions();
            });
        },
        
        async loadSuggestions() {
            const cmp = this;
            cmp.addModal.loadingSuggestions = true;
            
            let url = '/trust/scope/suggest';
            if (cmp.addModal.suggestionsSearch) {
                url += '?search=' + encodeURIComponent(cmp.addModal.suggestionsSearch);
            }
            
            await AjaxUtils.ajax_get({
                url: url,
                success: function(data) {
                    cmp.addModal.loadingSuggestions = false;
                    if (data.success) {
                        cmp.addModal.suggestions = data.suggestions || [];
                    } else {
                        cmp.$flash({ message: data.message, type: 'error' });
                    }
                },
                fail: function() {
                    cmp.addModal.loadingSuggestions = false;
                    cmp.$flash({ message: 'Failed to load suggestions', type: 'error' });
                },
                opts: {
                    dataType: 'json',
                    flash_form_errors: false,
                    flash_404_errors: false,
                    show_success_flash: false
                }
            });
        },
        
        toggleSuggestionSelection(suggestion) {
            const idx = this.addModal.selectedSuggestions.findIndex(
                s => s.apex_domain === suggestion.apex_domain
            );
            if (idx >= 0) {
                this.addModal.selectedSuggestions.splice(idx, 1);
            } else {
                this.addModal.selectedSuggestions.push(suggestion);
            }
        },
        
        isSuggestionSelected(suggestion) {
            return this.addModal.selectedSuggestions.some(
                s => s.apex_domain === suggestion.apex_domain
            );
        },
        
        selectAllSuggestions() {
            // Only select non-existing suggestions
            this.addModal.selectedSuggestions = this.addModal.suggestions.filter(
                s => !s.in_trust_scope
            ).slice();
        },
        
        clearSuggestionSelection() {
            this.addModal.selectedSuggestions = [];
        },
        
        debouncedSearchSuggestions: debounce(function() {
            this.loadSuggestions();
        }, 300),
        
        onSuggestionsSearchChange(value) {
            this.addModal.suggestionsSearch = value;
            this.debouncedSearchSuggestions();
        },
        
        async submitAllDomains() {
            const cmp = this;
            
            if (!cmp.canSubmit) {
                cmp.$flash({ message: 'Please select at least one domain', type: 'warning' });
                return;
            }
            
            cmp.submitting = true;
            
            const domains = cmp.allDomainsToAdd.map(d => d.apex_domain).join('\n');
            
            await AjaxUtils.ajax_post({
                url: '/trust/scope/add',
                data: JSON.stringify({ domains: domains }),
                success: function(data) {
                    cmp.submitting = false;
                    if (data.success) {
                        cmp.$flash({ message: data.message, type: 'success' });
                        cmp.closeAddModal();
                        cmp.loadScope();
                    } else {
                        cmp.$flash({ message: data.message, type: 'error' });
                    }
                },
                fail: function() {
                    cmp.submitting = false;
                    cmp.$flash({ message: 'Failed to add domains', type: 'error' });
                },
                opts: {
                    dataType: 'json',
                    contentType: 'application/json',
                    flash_form_errors: false,
                    flash_404_errors: false
                }
            });
        },
        
        goToManualEntry() {
            this.addModal.step = 'manual';
        },
        
        goToSuggestions() {
            this.addModal.step = 'suggest';
        },
        
        async goToConfirm() {
            if (!this.canProceedToConfirm) return;
            // Auto-validate manual domains if on manual step with text entered
            if (this.addModal.step === 'manual' && this.addModal.domains.trim()) {
                var valid = await this.validateDomains();
                if (!valid) return;
            }
            this.addModal.step = 'confirm';
        },
        
        closeAddModal() {
            this.addModal.open = false;
            this.$nextTick(() => {
                this.addModal.render = false;
            });
        },
        
        async validateDomains() {
            const cmp = this;
            
            if (!cmp.addModal.domains.trim()) {
                cmp.$flash({ message: 'Please enter at least one domain', type: 'warning' });
                return false;
            }
            
            cmp.addModal.validating = true;
            cmp.addModal.validationComplete = false;
            
            var allValid = false;
            await AjaxUtils.ajax_post({
                url: '/trust/scope/validate',
                data: JSON.stringify({ domains: cmp.addModal.domains }),
                success: function(data) {
                    cmp.addModal.validating = false;
                    if (data.success) {
                        cmp.addModal.validationResults = data.results || [];
                        cmp.addModal.validationComplete = true;
                        var hasInvalid = cmp.addModal.validationResults.some(function(r) { return !r.valid; });
                        allValid = !hasInvalid && cmp.addModal.validationResults.length > 0;
                    } else {
                        cmp.$flash({ message: data.message, type: 'error' });
                    }
                },
                fail: function() {
                    cmp.addModal.validating = false;
                    cmp.$flash({ message: 'Failed to validate domains', type: 'error' });
                },
                opts: {
                    dataType: 'json',
                    contentType: 'application/json',
                    flash_form_errors: false,
                    flash_404_errors: false
                }
            });
            return allValid;
        },
        
        removeValidationResult(index) {
            this.addModal.validationResults.splice(index, 1);
        },
        
        removeSuggestion(suggestion) {
            const idx = this.addModal.selectedSuggestions.findIndex(
                s => s.apex_domain === suggestion.apex_domain
            );
            if (idx >= 0) {
                this.addModal.selectedSuggestions.splice(idx, 1);
            }
        },
        
        toggleEnabled(row) {
            const cmp = this;
            const scopeId = row.get('_id');
            const currentState = row.get('enabled');
            
            AjaxUtils.ajax_post({
                url: `/trust/scope/update/${scopeId}`,
                data: JSON.stringify({ enabled: !currentState }),
                success: function(data) {
                    if (data.success) {
                        cmp.$flash({ message: 'Scope entry updated', type: 'success' });
                        cmp.loadScope();
                    } else {
                        cmp.$flash({ message: data.message, type: 'error' });
                    }
                },
                fail: function() {
                    cmp.$flash({ message: 'Failed to update scope entry', type: 'error' });
                },
                opts: {
                    dataType: 'json',
                    contentType: 'application/json',
                    flash_form_errors: false,
                    flash_404_errors: false
                }
            });
        },
        
        removeScope(row) {
            const cmp = this;
            const scopeId = row.get('_id');
            const domain = row.get('apex_domain');
            
            cmp.$dialog.confirm(
                `Are you sure you want to remove "${domain}" from your Trust Monitor scope?`,
                'Remove Domain',
                function() {
                    AjaxUtils.ajax_get({
                        url: `/trust/scope/remove/${scopeId}`,
                        success: function(data) {
                            if (data.success) {
                                cmp.$flash({ message: data.message, type: 'success' });
                                cmp.loadScope();
                            } else {
                                cmp.$flash({ message: data.message, type: 'error' });
                            }
                        },
                        fail: function() {
                            cmp.$flash({ message: 'Failed to remove domain', type: 'error' });
                        },
                        opts: {
                            dataType: 'json',
                            flash_form_errors: false,
                            flash_404_errors: false
                        }
                    });
                }
            );
        },
        
        reloadTable() {
            if (this.$refs.scopeTable) {
                this.$refs.scopeTable.store.reload();
            }
            this.loadScope();
        },
        
        loadOsintScanningSetting() {
            var cmp = this;
            cmp.osintScanningLoading = true;
            AjaxUtils.ajax_get({
                url: '/trust/settings/view',
                success: function(data) {
                    cmp.osintScanningLoading = false;
                    if (data.success && data.data) {
                        cmp.osintScanningEnabled = !!data.data.enable_osint_scanning;
                    }
                },
                fail: function() {
                    cmp.osintScanningLoading = false;
                },
                opts: { dataType: 'json', flash_form_errors: false, flash_404_errors: false, show_success_flash: false }
            });
        },
        
        toggleOsintScanning(value) {
            var cmp = this;
            cmp.osintScanningLoading = true;
            AjaxUtils.ajax_post({
                url: '/trust/settings/update',
                data: JSON.stringify({ enable_osint_scanning: value }),
                success: function(data) {
                    cmp.osintScanningLoading = false;
                    if (data.success) {
                        cmp.osintScanningEnabled = value;
                        cmp.$flash({ message: 'OSINT Scanning ' + (value ? 'enabled' : 'disabled'), type: 'success' });
                    } else {
                        cmp.$flash({ message: data.message, type: 'error' });
                    }
                },
                fail: function() {
                    cmp.osintScanningLoading = false;
                    cmp.$flash({ message: 'Failed to update OSINT Scanning setting', type: 'error' });
                },
                opts: { dataType: 'json', contentType: 'application/json', flash_form_errors: false, flash_404_errors: false }
            });
        }
    },
    mounted() {
        this.loadScope();
        this.loadOsintScanningSetting();
        
        // Check for openModal query parameter to auto-open the add modal
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('openModal') === 'true') {
            // Small delay to ensure component is fully loaded
            this.$nextTick(() => {
                this.openAddModal();
                // Remove the query param from URL without page reload
                const newUrl = window.location.pathname;
                window.history.replaceState({}, document.title, newUrl);
            });
        }
    },
    template: `
        <ui-page title="Trust Monitor Scope" stickyTitle>
            <template #title-bar-buttons>
                <ui-split-button
                    :label="osintScanningEnabled ? 'OSINT Scanning Enabled' : 'OSINT Scanning Disabled'"
                    :iconLeft="osintScanningEnabled ? 'fa-circle-check' : 'fa-circle-xmark'"
                    :type="osintScanningEnabled ? 'primary' : 'outline'"
                    :disabled="osintScanningLoading"
                    secondaryLabel="Scan Settings"
                    secondaryIcon="fa-cog"
                    secondaryUrl="/trust/settings?tab=scan"
                    @click="toggleOsintScanning(!osintScanningEnabled)"
                />
                <ui-button
                    label="Add Scope"
                    iconLeft="fa-plus"
                    @click="openAddModal"
                />
            </template>
            
            <template #content>
                <ui-layout direction="column" gap="4">
                    <!-- Licence info panel -->
                    <ui-panel-base v-if="!licence.unlimited && scopeItems.length > 0">
                        <ui-layout align="center" gap="4">
                            <ui-icon icon="fa-info-circle" type="info" />
                            <span>
                                You have <strong>{{ licence.used_trust_scope }}</strong> of 
                                <strong>{{ licence.max_trust_scope }}</strong> Trust Monitor scope domains in use.
                            </span>
                        </ui-layout>
                    </ui-panel-base>
                    
                    <!-- Empty state -->
                    <apck-trust-scope-empty
                        v-if="!loading && scopeItems.length === 0"
                        title="No Trust Monitor Scope Configured"
                        buttonLabel="Add Your First Domain"
                        @action="openAddModal"
                    />
                    
                    <!-- Scope table -->
                    <apck-table
                        v-if="!loading && scopeItems.length > 0"
                        ref="scopeTable"
                        :columns="cols"
                        :actions="actions"
                        src="/trust/scope/list"
                        :limit="50"
                        :border="false"
                        :show-toolbar="false"
                        newStyleTable
                    />
                </ui-layout>
                
                <!-- Add Modal -->
                <ui-modal
                    v-if="addModal.render"
                    title="Add Trust Monitor Scope"
                    size="lg"
                    align="center"
                    :open.sync="addModal.open"
                    @close="closeAddModal"
                >
                    <template #content>
                        <div class="scope-wizard-modal">
                            <!-- Wizard-style step tabs -->
                            <div class="scope-wizard wizard-panel">
                                <ul class="steps">
                                    <li :class="{ active: addModal.step === 'suggest' }">
                                        <a @click="!submitting && goToSuggestions()">
                                            <i class="fa-regular fa-lightbulb"></i>&nbsp; Suggested Domains
                                        </a>
                                    </li>
                                    <li :class="{ active: addModal.step === 'manual' }">
                                        <a @click="!submitting && goToManualEntry()">
                                            <i class="fa-regular fa-keyboard"></i>&nbsp; Manual Entry
                                        </a>
                                    </li>
                                    <li :class="{ active: addModal.step === 'confirm', disabled: !canProceedToConfirm }">
                                        <a @click="!submitting && canProceedToConfirm && goToConfirm()">
                                            <i class="fa-regular fa-clipboard-check"></i>&nbsp; Confirm
                                        </a>
                                    </li>
                                </ul>
                                
                                <div class="wizard-content">
                                    <div class="wizard-pane-container">
                                        <!-- Licence warning if near limit -->
                                        <ui-alert v-if="!licence.unlimited && remaining < 10" state="warning" class="mb-4">
                                            You have {{ remaining }} domain slots remaining.
                                        </ui-alert>
                                        
                                        <!-- Suggest Step -->
                                        <div class="wizard-pane" :class="{ active: addModal.step === 'suggest' }">
                                            <p class="wizard-secondary-text mb-4">
                                                Based on your existing scan scope and discovered assets, 
                                                we recommend adding the following apex domains to Trust Monitor.
                                            </p>
                                            
                                            <ui-input-text
                                                class="suggestions-search mb-3"
                                                placeholder="Search domains..."
                                                type="text"
                                                :modelValue="addModal.suggestionsSearch"
                                                @update:modelValue="onSuggestionsSearchChange"
                                            />
                                            
                                            <ui-spinner :show="addModal.loadingSuggestions" />
                                            
                                            <template v-if="!addModal.loadingSuggestions">
                                                <ui-alert v-if="addModal.suggestions.length === 0 && !addModal.suggestionsSearch" state="info">
                                                    No suggestions available. Add domains to your scan scope or run Asset Discovery first, 
                                                    or use manual entry to add domains directly.
                                                </ui-alert>
                                                <ui-alert v-else-if="addModal.suggestions.length === 0 && addModal.suggestionsSearch" state="info">
                                                    No domains match your search.
                                                </ui-alert>
                                                
                                                <template v-if="addModal.suggestions.length > 0">
                                                    <ui-layout justify="space-between" align="center" class="mb-3">
                                                        <span class="wizard-secondary-text">
                                                            <strong>{{ addModal.selectedSuggestions.length }}</strong> of 
                                                            <strong>{{ addModal.suggestions.filter(s => !s.in_trust_scope).length }}</strong> 
                                                            available domains selected
                                                        </span>
                                                        <ui-layout gap="2">
                                                            <ui-button
                                                                label="Select All"
                                                                type="ghost"
                                                                size="sm"
                                                                @click="selectAllSuggestions"
                                                            />
                                                            <ui-button
                                                                label="Clear"
                                                                type="ghost"
                                                                size="sm"
                                                                @click="clearSuggestionSelection"
                                                            />
                                                        </ui-layout>
                                                    </ui-layout>
                                                    
                                                    <div class="suggestions-table-wrapper">
                                                        <table class="validation-table">
                                                            <thead>
                                                                <tr>
                                                                    <th class="action-column"></th>
                                                                    <th>Apex Domain</th>
                                                                    <th>Source</th>
                                                                    <th>Targets</th>
                                                                    <th>Status</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr 
                                                                    v-for="suggestion in addModal.suggestions"
                                                                    :key="suggestion.apex_domain"
                                                                    :class="{
                                                                        'validation-success': isSuggestionSelected(suggestion),
                                                                        'row-disabled': suggestion.in_trust_scope
                                                                    }"
                                                                    @click="!suggestion.in_trust_scope && toggleSuggestionSelection(suggestion)"
                                                                >
                                                                    <td class="action-column">
                                                                        <input 
                                                                            type="checkbox"
                                                                            :checked="isSuggestionSelected(suggestion)"
                                                                            :disabled="suggestion.in_trust_scope"
                                                                            @click.stop="!suggestion.in_trust_scope && toggleSuggestionSelection(suggestion)"
                                                                        />
                                                                    </td>
                                                                    <td class="target-cell">
                                                                        <i class="fa-regular fa-globe icon"></i>
                                                                        {{ suggestion.apex_domain }}
                                                                    </td>
                                                                    <td>
                                                                        <span 
                                                                            v-for="source in suggestion.sources" 
                                                                            :key="source"
                                                                            class="source-tag"
                                                                            :class="'source-tag--' + source.toLowerCase()"
                                                                        >{{ source }}</span>
                                                                    </td>
                                                                    <td>
                                                                        <span class="wizard-secondary-text">{{ suggestion.total_targets }} target(s)</span>
                                                                    </td>
                                                                    <td>
                                                                        <span v-if="suggestion.in_trust_scope" class="success-message">
                                                                            <i class="fa-regular fa-check-circle icon"></i> Already in scope
                                                                        </span>
                                                                        <span v-else class="wizard-secondary-text">Available</span>
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </template>
                                            </template>
                                        </div>
                                        
                                        <!-- Manual Entry Step -->
                                        <div class="wizard-pane" :class="{ active: addModal.step === 'manual' }">
                                            <p class="wizard-secondary-text mb-4">
                                                Enter apex domains (one per line) to add to your Trust Monitor scope.
                                                These domains will be scanned for subdomain discovery and third-party trust analysis.
                                            </p>
                                            <br />
                                            <ui-panel-accordion title="What is an apex domain?" titleSize="sm" :defaultOpen="false" class="mb-4">
                                                <ui-layout direction="column" gap="2">
                                                    <p class="wizard-secondary-text">
                                                        An apex domain is the root domain without any subdomain prefix. 
                                                        Trust Monitor scans for subdomains and third-party dependencies across these domains.
                                                    </p>
                                                    
                                                    <pre class="wizard-code-block">example.com
mycompany.co.uk
brand.io</pre>
                                                    <p class="wizard-secondary-text">
                                                        The above are examples of apex domains.
                                                    </p>
                                                </ui-layout>
                                            </ui-panel-accordion>
                                            
                                            <ui-layout direction="column" gap="2">
                                                <label>Apex Domains</label>
                                                <textarea
                                                    v-model="addModal.domains"
                                                    class="form-control"
                                                    rows="6"
                                                    placeholder="example.com\nmycompany.co.uk\nbrand.io"
                                                    :disabled="addModal.validating || submitting"
                                                ></textarea>
                                            </ui-layout>
                                            
                                            <!-- Validation results -->
                                            <ui-layout v-if="addModal.validationComplete" direction="column" gap="2" class="mt-4">
                                                <ui-heading tag="h4">Validation Results</ui-heading>
                                                
                                                <ui-alert v-if="validDomainsCount > 0" state="success">
                                                    {{ validDomainsCount }} domain(s) ready to add
                                                </ui-alert>
                                                
                                                <ui-alert v-if="invalidDomainsCount > 0" state="destructive">
                                                    {{ invalidDomainsCount }} domain(s) have errors
                                                </ui-alert>
                                                
                                                <div class="suggestions-table-wrapper">
                                                    <table class="validation-table">
                                                        <thead>
                                                            <tr>
                                                                <th>Domain</th>
                                                                <th>Status</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr 
                                                                v-for="(result, idx) in addModal.validationResults" 
                                                                :key="idx"
                                                                :class="{ 
                                                                    'validation-success': result.valid, 
                                                                    'validation-error': !result.valid 
                                                                }"
                                                            >
                                                                <td class="target-cell">
                                                                    <i class="fa-regular fa-globe icon"></i>
                                                                    {{ result.apex_domain || result.original }}
                                                                </td>
                                                                <td>
                                                                    <span v-if="result.valid" class="success-message">
                                                                        <i class="fa-regular fa-check-circle icon"></i> Ready to add
                                                                    </span>
                                                                    <span v-else class="error-message">
                                                                        <i class="fa-regular fa-times-circle icon"></i> {{ result.error }}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </ui-layout>
                                        </div>
                                        
                                        <!-- Confirm Step -->
                                        <div class="wizard-pane" :class="{ active: addModal.step === 'confirm' }">
                                            <ui-layout direction="column" gap="4">
                                                <ui-layout direction="column" gap="2">
                                                    <ui-layout gap="2" align="center">
                                                        <ui-icon icon="fa-clipboard-check" type="warning" />
                                                        <ui-heading tag="h4">Review Your Scope</ui-heading>
                                                    </ui-layout>
                                                    <p class="wizard-secondary-text">
                                                        Please review the apex domains you're adding to Trust Monitor scope.
                                                    </p>
                                                </ui-layout>
                                                
                                                <ui-alert v-if="allDomainsToAdd.length === 0" state="info">
                                                    No domains selected. Please go back and select suggested domains or add domains manually.
                                                </ui-alert>
                                                
                                                <ui-alert 
                                                    v-if="!licence.unlimited && allDomainsToAdd.length > remaining" 
                                                    state="destructive"
                                                >
                                                    You are trying to add {{ allDomainsToAdd.length }} domains but only have {{ remaining }} slots remaining.
                                                </ui-alert>
                                                
                                                <template v-if="allDomainsToAdd.length > 0">
                                                    <ui-layout direction="column" gap="2">
                                                        <ui-heading tag="h5">
                                                            <ui-icon icon="fa-globe" type="primary" />
                                                            {{ allDomainsToAdd.length }} Domain(s) to Add
                                                        </ui-heading>
                                                        
                                                        <div class="suggestions-table-wrapper">
                                                            <table class="validation-table summary-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Apex Domain</th>
                                                                        <th>Source</th>
                                                                        <th class="action-column">Action</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    <tr 
                                                                        v-for="domain in allDomainsToAdd" 
                                                                        :key="domain.apex_domain"
                                                                        class="validation-success"
                                                                    >
                                                                        <td class="target-cell">
                                                                            <i class="fa-regular fa-globe icon"></i>
                                                                            {{ domain.apex_domain }}
                                                                        </td>
                                                                        <td>
                                                                            <span v-if="domain.source === 'Suggested'" class="source-tag source-tag--suggested">
                                                                                Suggested
                                                                            </span>
                                                                            <span v-else class="source-tag source-tag--manual">
                                                                                Manual
                                                                            </span>
                                                                            <template v-if="domain.sources && domain.sources.length > 0">
                                                                                <span 
                                                                                    v-for="src in domain.sources" 
                                                                                    :key="src"
                                                                                    class="source-tag"
                                                                                    :class="'source-tag--' + src.toLowerCase()"
                                                                                >{{ src }}</span>
                                                                            </template>
                                                                        </td>
                                                                        <td class="action-column">
                                                                            <ui-button
                                                                                label="Remove"
                                                                                iconLeft="fa-trash"
                                                                                color="red"
                                                                                size="sm"
                                                                                type="ghost"
                                                                                @click="domain.source === 'Suggested' ? removeSuggestion(domain) : removeValidationResult(validatedManualDomains.findIndex(r => (r.apex_domain || r.original) === domain.apex_domain))"
                                                                                :disabled="submitting"
                                                                            />
                                                                        </td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </ui-layout>
                                                </template>
                                            </ui-layout>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                    
                    <template #footer>
                        <ui-layout justify="end" gap="2">
                            <ui-button
                                label="Cancel"
                                type="outline"
                                @click="closeAddModal"
                                :disabled="addModal.validating || submitting"
                            />
                            
                            <!-- Suggest step buttons -->
                            <template v-if="addModal.step === 'suggest'">
                                <ui-button
                                    label="Continue to Manual Entry"
                                    iconRight="fa-arrow-right"
                                    @click="goToManualEntry"
                                />
                            </template>
                            
                            <!-- Manual step buttons -->
                            <template v-if="addModal.step === 'manual'">
                                <ui-button
                                    v-if="!addModal.validationComplete"
                                    label="Validate Domains"
                                    type="outline"
                                    :loading="addModal.validating"
                                    @click="validateDomains"
                                />
                                <ui-button
                                    label="Proceed to Confirm"
                                    iconRight="fa-arrow-right"
                                    @click="goToConfirm"
                                />
                            </template>
                            
                            <!-- Confirm step buttons -->
                            <template v-if="addModal.step === 'confirm'">
                                <ui-button
                                    label="Back"
                                    type="outline"
                                    iconLeft="fa-arrow-left"
                                    @click="goToSuggestions"
                                    :disabled="submitting"
                                />
                                <ui-button
                                    label="Add Domains to Trust Monitor"
                                    :loading="submitting"
                                    :disabled="!canSubmit"
                                    @click="submitAllDomains"
                                />
                            </template>
                        </ui-layout>
                    </template>
                </ui-modal>
                
                <ui-spinner :show="loading" theme="dark" />
            </template>
        </ui-page>
    `
};
